import React, { Fragment, useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import SearchWithFilter from '../common/SearchWithFilter'
import EmptyPageResults from '../common/EmptyPageResults'
import LoadingSpinner from '../common/LoadingSpinner'
import Dropdown from '../common/Dropdown'
import SimpleButton from '../common/SimpleButton'
import GenericTable from '../common/GenericTable'
import ActionsMenu from '../common/ActionsMenu'
import ConfirmationModal from '../common/ConfirmationModal'
import { toast } from '../common/Toast'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, PendingIcon, DisabledIcon, InformationIcon } from '../../icons'
import { loadRequests, changeRequestStatus, clearMembersMessages, loadCurrentCompany } from '../../actions/members'
import { loadCompanyRoles } from '../../actions/companies'
import { organizationRequestsWritePermission } from '../../utils/permissionValidation'
import { drAuthRules } from '../../utils/drConfig'
import { convertTimeWithTAndZ } from '../../utils/filters/date'
import { useWindowSize } from '../../libs/hooks'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'

const orderingFields = [
  {
    field: 'email',
    order: 'asc',
    translationKey: 'email_asc',
  },
  {
    field: 'email',
    order: 'desc',
    translationKey: 'email_desc',
  },
  {
    field: 'lastModifyAt',
    order: 'asc',
    translationKey: 'date_asc',
  },
  {
    field: 'lastModifyAt',
    order: 'desc',
    translationKey: 'date_desc',
  },
  {
    field: 'requestType',
    order: 'asc',
    translationKey: 'request_type_asc',
  },
  {
    field: 'requestType',
    order: 'desc',
    translationKey: 'request_type_desc',
  },
  {
    field: 'status',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'status',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const Requests = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { hasEditAccess: hasEditAccessProp } = props
  const hasEditAccess = hasEditAccessProp && drAuthRules.isCompanyAccountUpdateAllowed
  const [requestsTableData, setRequestsTableData] = useState([])
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [statusesFilter, setStatusesFilter] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [showAcceptConfirmationModal, setShowAcceptConfirmationModal] = useState(false)
  const [requestIdToAccept, setRequestIdToAccept] = useState(null)
  const [currentRoleName, setCurrentRoleName] = useState('')
  const [newRoleIdToAccept, setNewRoleIdToAccept] = useState(null)
  const [roleOptions, setRoleOptions] = useState([])
  const [expandedRequest, setExpandedRequest] = useState(0)
  const requestsStatuses = ['invited', 'requested', 'expired']

  useLayoutEffect(() => {
    if (!props.companyId) {
      props.actions.loadCurrentCompany()
    }
  }, [props.actions])

  useEffect(() => {
    if (!props.companyId) {
      return
    }

    if (props.companyRoles.length === 0) {
      props.actions.loadCompanyRoles(props.companyId)
    }
  }, [props.companyId])

  useEffect(() => {
    if (props.companyRoles.length > 0) {
      const mappedRoles = props.companyRoles.map((role) => ({
        key: role.id,
        value: role.name.replace(/Dealer |Dentist /g, ''),
      }))

      setRoleOptions(mappedRoles)
    }
  }, [props.companyRoles])

  useEffect(() => {
    const tableData = props.requests.map((request, index) => {
      return {
        index,
        identifier: request.id,
        email: request.email,
        date: convertTimeWithTAndZ(request.lastModifyAt),
        requestType: request.requestType,
        status: request.status,
        actions: hasEditAccess
          ? [
              ...(request.status === 'requested'
                ? [
                    {
                      label: 'approve',
                      onActionClick: (e) => {
                        e.stopPropagation()
                        openAcceptConfirmationModal(request.id, request.role?.name)
                      },
                      requiredPermission: organizationRequestsWritePermission,
                    },
                    {
                      label: 'reject',
                      onActionClick: (e) => {
                        e.stopPropagation()
                        handleChangeRequestStatus(request.id, 'reject')
                      },
                      requiredPermission: organizationRequestsWritePermission,
                    },
                  ]
                : []),
              ...(request.status === 'expired'
                ? [
                    {
                      label: 'resend',
                      onActionClick: (e) => {
                        e.stopPropagation()
                        handleChangeRequestStatus(request.id, 'resend')
                      },
                      requiredPermission: organizationRequestsWritePermission,
                    },
                  ]
                : []),
              ...(request.status === 'invited' || request.status === 'expired'
                ? [
                    {
                      label: request.status === 'invited' ? 'cancel' : 'remove',
                      onActionClick: (e) => {
                        e.stopPropagation()
                        handleChangeRequestStatus(request.id, 'cancel')
                      },
                      requiredPermission: organizationRequestsWritePermission,
                    },
                  ]
                : []),
            ]
          : [],
      }
    })

    setRequestsTableData(tableData)
  }, [props.requests])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearMembersMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearMembersMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadRequests(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters && props.companyId) {
          setSelectedPage(1)
          handleLoadRequests(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [props.companyId, searchParam, statusesFilter, orderBy])

  const handleLoadRequests = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      offset: withReset ? 0 : (selectedPage - 1) * process.env.REACT_APP_PAGINATION_SIZE,
      limit: process.env.REACT_APP_PAGINATION_SIZE,
    }

    props.actions.loadRequests(props.companyId, queryParams, withReset).finally(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setStatusesFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (statusesFilter.length > 0) {
      queryParams = { ...queryParams, status: statusesFilter }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const openAcceptConfirmationModal = (id, roleName) => {
    setRequestIdToAccept(id)
    setCurrentRoleName(roleName)
    setShowAcceptConfirmationModal(true)
  }

  const handleChangeRequestStatus = (requestId, action) => {
    if (!props.companyId) {
      return
    }

    props.actions.changeRequestStatus(props.companyId, requestId, {
      action: action,
      ...(action === 'accept' && newRoleIdToAccept && { roleId: newRoleIdToAccept }),
    })
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  const renderRequestStatus = (status) => {
    switch (status) {
      case requestsStatuses[0]:
        return (
          <div className="status-pending">
            <PendingIcon />
            <p>{t('invited')}</p>
          </div>
        )
      case requestsStatuses[1]:
        return (
          <div className="status-pending">
            <PendingIcon />
            <p>{t('requested')}</p>
          </div>
        )
      case requestsStatuses[2]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('expired')}</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <Fragment>
      <div className="requests-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={statusesFilter.length > 0}
            counterBadge={statusesFilter.length}
          />
        </div>
        {isMobile && requestsTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && requestsTableData.length > 0 && (
          <GenericTable
            data={requestsTableData}
            headers={[
              {
                title: t('date'),
                orderKey: 'lastModifyAt',
              },
              {
                title: t('email'),
                orderKey: 'email',
              },
              {
                title: t('request_type'),
                orderKey: 'requestType',
              },
              {
                title: t('dealer-portal.request_status'),
                orderKey: 'status',
              },
              {
                title: '',
              },
            ]}
            keys={['date', 'email', 'requestType', 'status', 'action']}
            keyRenderer={{
              requestType: (item) => {
                return <>{t([`dealer-portal.${item.requestType}`, item.requestType])}</>
              },
              status: (item) => {
                return <div className="status-wrapper">{renderRequestStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <>
                    {item.actions.length > 0 && (
                      <div
                        className="table-actions-wrapper"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleShowActionsMenu(item['index'])
                        }}
                      >
                        {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                        {showActionsMenu === item['index'] && (
                          <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                        )}
                      </div>
                    )}
                  </>
                )
              },
            }}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          requestsTableData.map((request, index) => (
            <div key={index} className="card" onClick={() => setExpandedRequest(index)}>
              <div className={'card-item' + (expandedRequest !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('email')}</div>
                <div className={'card-item-body' + (expandedRequest !== index ? ' align-center' : '')}>
                  <div>{request.email}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={request.actions} isLoading={showSpinner} />
                    )}
                  </div>
                </div>
              </div>
              {expandedRequest === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('date')}</div>
                    <div className="card-item-body">{request.date}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('request_type')}</div>
                    <div className="card-item-body">{t([`dealer-portal.${request.requestType}`, request.requestType])}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('dealer-portal.request_status')}</div>
                    <div className="card-item-body card-item-body-status">{renderRequestStatus(request.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && requestsTableData.length === 0 && (
          <EmptyPageResults title={t('dealer-portal.no_requests_found')} subtitle={t('dealer-portal.no_requests_found_subtitle')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('dealer-portal.request_status'),
              type: FilterType.multiSelect,
              dataset: requestsStatuses,
              input: statusesFilter,
              output: (filterStatuses) => {
                setStatusesFilter(filterStatuses)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showAcceptConfirmationModal && (
        <ConfirmationModal
          className="accept-member-confirmation"
          onToggle={() => {
            setNewRoleIdToAccept(null)
            setShowAcceptConfirmationModal(false)
          }}
          message={t('dealer-portal.accept_member_confirmation')}
          onCancel={() => {
            setNewRoleIdToAccept(null)
            setShowAcceptConfirmationModal(false)
          }}
          onAccept={() => {
            handleChangeRequestStatus(requestIdToAccept, 'accept')
            setShowAcceptConfirmationModal(false)
          }}
          acceptButtonText={t('confirm')}
          requiredPermission={organizationRequestsWritePermission}
        >
          <div className="member-confirmation-wrapper">
            <div className="disclaimer">
              <p>{t('dealer-portal.accept_member_disclaimer')}</p>
            </div>
            <div className="title">{t('dealer-portal.current_role')}</div>
            <input type="text" className={'input-item'} value={currentRoleName?.split('-')[0] || ''} disabled />
            <div className="title">
              {`${t('dealer-portal.new_role')} (${t('optional')})`}
              <InformationIcon onClick={() => props.showRoleDetails()} />
            </div>
            <Dropdown options={roleOptions} onChange={(option) => setNewRoleIdToAccept(option.key)} placeholder={t('dealer-portal.select_new_role')} />
          </div>
        </ConfirmationModal>
      )}
    </Fragment>
  )
}

function stateToProps({ members, companies }) {
  return {
    companyId: members?.companyId,
    requests: members?.requests || [],
    isLoading: members?.isLoading || companies?.isLoading,
    canLoadMore: members?.canLoadMoreRequests,
    errorMessage: members?.errorMessage,
    successMessage: members?.successMessage,
    companyRoles: companies?.roles || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadCurrentCompany,
        loadRequests,
        loadCompanyRoles,
        changeRequestStatus,
        clearMembersMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Requests)

